/**
 * The mark for one AI app, and which of its two forms a given ground can show.
 *
 * Split out of `source-icons.tsx` — which is a client module pulling in
 * `@iconify/react` and the whole integrations registry — so the CHOICE is a
 * plain function a unit test can call. The choice is what broke: the picker's
 * selected pill sets a near-black ground, and `logos:openai-icon` is a path
 * with no `fill` attribute, so it paints SVG-default black on black. Nothing
 * rendered wrong, nothing threw; the logo was simply gone. The attempted fix
 * before this one was `brightness-125` on the icon, which multiplies black by
 * 1.25 and yields black.
 *
 * So each entry carries two forms:
 *
 * - `name` — the colour mark, for a light ground. `logos:` where the vendor
 *   has a colour lockup worth showing.
 * - `mono` — a `simple-icons:` mark for a dark ground. Every icon in that set
 *   is a single path painted `currentColor`, which is the property being
 *   relied on: on a dark ground the mark inherits the text colour that was
 *   already chosen to be legible there, instead of carrying a fixed colour
 *   that may or may not survive the swap.
 *
 * `tint` is dropped on a dark ground on purpose. It exists to give a
 * monochrome mark its brand colour on white; keeping it would put one orange
 * mark in a row of white ones, and the ground this is for (a selected pill, a
 * logo row over a painting) reads as one object rather than six.
 *
 * Names verified to resolve against api.iconify.design on 2026-08-30 —
 * an Iconify name that does not exist renders an empty box, which in a row of
 * logos is indistinguishable from the bug this file exists to fix.
 */

export interface HostMark {
  /** The Iconify name to render. */
  name: string
  /** A colour class for a monochrome mark, when the ground is light. */
  tint?: string
}

interface HostMarkEntry {
  name: string
  tint?: string
  /**
   * The `simple-icons:` form, when `name` is not already monochrome.
   *
   * Absent means `name` is itself a `currentColor` mark and serves both
   * grounds; `resolveHostMark` enforces that rather than trusting it.
   */
  mono?: string
}

/**
 * Iconify name per AI app, or `null` for the ones with no published mark.
 *
 * `simple-icons:claude`, NOT `logos:claude`: the latter is the full wordmark
 * lockup, which at 24px renders as an illegible grey smudge next to six square
 * glyphs. Verified in a browser, not assumed — a name that resolves is not the
 * same as one that reads at the size it is used.
 */
const HOST_MARKS: Record<string, HostMarkEntry | null> = {
  'claude-code': { name: 'simple-icons:claude', tint: 'text-[#D97757]' },
  'claude-web': { name: 'simple-icons:claude', tint: 'text-[#D97757]' },
  // The pair this file exists for: `logos:openai-icon` is fixed black.
  chatgpt: { name: 'logos:openai-icon', mono: 'simple-icons:openai' },
  codex: { name: 'logos:openai-icon', mono: 'simple-icons:openai' },
  cursor: { name: 'simple-icons:cursor' },
  // Not black, but `logos:visual-studio-code` is #0065A9 — a mid blue that
  // clears 3:1 against white and roughly 2:1 against this page's near-black.
  vscode: {
    name: 'logos:visual-studio-code',
    mono: 'simple-icons:visualstudiocode',
  },
  custom: null,
}

/**
 * The mark to render for `hostId` on the ground it is being rendered on.
 *
 * `null` means "this app has no published mark" — the caller draws its own
 * fallback glyph, which inherits `currentColor` and therefore needs no dark
 * form of its own.
 */
export function resolveHostMark(
  hostId: string,
  { onDark = false }: { onDark?: boolean } = {},
): HostMark | null {
  const entry = HOST_MARKS[hostId]
  if (!entry) return null
  if (!onDark) return { name: entry.name, tint: entry.tint }
  return { name: entry.mono ?? entry.name }
}
